import { el, fragment } from '../dom.js';
import { admin, adminRaw } from '../admin-api.js';
import { t, formatMoment } from '../i18n.js';
import { runsChart } from '../charts.js';

// What the pipeline did, what it is sitting on, and what is wrong with it.
//
// The four numbers are the ones the run itself cannot give: a pass counts what
// it WROTE, so a second run stages nothing and reads as "nothing to do" when it
// means "nothing new". That is how 35 events once sat promotable and unnoticed.
export async function overviewPanel({ reload }) {
  const [data, sources] = await Promise.all([
    admin('GET', '/crawl/runs'),
    admin('GET', '/crawl/sources'),
  ]);
  const pending = data.pending ?? { pending: 0, promotable: 0, noTime: 0, noVenue: 0 };

  return fragment([
    starter(data, reload),
    el('div', { class: 'card' }, [
      el('h3', { class: 'card-title' }, t.crawlPending(pending.pending)),
      el('p', {}, t.crawlPromotable(pending.promotable)),
      el('p', { class: 'card-meta' }, [
        t.crawlNoTimeCount(pending.noTime),
        ` · ${t.crawlNoVenueCount(pending.noVenue)}`,
      ].join('')),
    ]),
    problems(sources?.sources ?? []),
    el('h2', { class: 'bucket-title' }, t.crawlRuns),
    runsChart(data.runs ?? [], { label: t.crawlTabOverview }),
    runList(data.runs ?? []),
  ]);
}

function starter(data, reload) {
  if (!data.enabled) {
    return el('div', { class: 'pub-empty' }, [
      el('p', {}, t.crawlOff),
      el('p', { class: 'card-meta' }, t.crawlOffHint),
    ]);
  }

  if (data.running) {
    // A run in flight is a state, not a disabled button: the difference matters
    // because a greyed-out control reads as a permission problem.
    return el('div', { class: 'card' }, el('p', { class: 'card-meta' },
      data.running.status === 'running'
        ? t.crawlRunning(formatMoment(data.running.started_at))
        : t.crawlQueued));
  }

  const button = el('button', { type: 'button', class: 'btn btn-primary' }, t.crawlStart);
  button.addEventListener('click', async () => {
    button.disabled = true;
    await adminRaw('POST', '/crawl/run', { only: [] });
    await reload();
  });
  return el('div', { class: 'card' }, [button, el('p', { class: 'card-meta' }, t.crawlStartHint)]);
}

// The failure that hides: a relaunched venue site does not error. The fetch
// succeeds, the parse finds nothing, and the run reports ok — so a source that
// used to deliver and now delivers nothing is the loudest thing on this page.
//
// Two rules learned from the first render against real data, where this panel
// came out as thirty-eight cards for thirty sources:
//
//   - one card per source, carrying its WORST problem. A source listed once as
//     stalled and again as failing is the same source twice, and the second
//     copy pushes a different source off the screen.
//   - a condition true of every source is a project state, not an alarm. All
//     thirty carry no robots/TDM record, so it is one line with a count. It
//     still has to be visible — it is the precondition the whole crawl runs on
//     — but as a sentence, because thirty identical cards read as noise and
//     get scrolled past, which is the same as hiding it.
function problems(sources) {
  const live = sources.filter((s) => s.health !== 'disabled');
  const unchecked = live.filter((s) => s.unchecked);

  const worst = live
    .map((source) => {
      if (source.health === 'stalled') return { source, rank: 0, message: t.crawlHealthStalled };
      if (source.consecutiveFailures > 0) {
        return { source, rank: 1, message: String(source.lastError ?? '').trim() || t.crawlHealthNever };
      }
      return null;
    })
    .filter(Boolean)
    .sort((a, b) => a.rank - b.rank || b.source.yieldTotal - a.source.yieldTotal);

  return fragment([
    el('h2', { class: 'bucket-title' }, t.crawlProblems),
    worst.length
      ? el('ul', { class: 'card-list' }, worst.map(({ source, message }) => row(source, message)))
      : el('div', { class: 'pub-empty' }, t.crawlNoProblems),
    unchecked.length
      ? el('div', { class: 'card' }, [
          el('h4', { class: 'card-title' }, t.crawlUncheckedCount(unchecked.length, live.length)),
          el('p', { class: 'card-meta' }, t.crawlUncheckedHint),
        ])
      : '',
  ].filter(Boolean));
}

function row(source, message) {
  return el('li', {}, el('div', { class: 'card' }, [
    el('div', { class: 'row' }, [
      el('h4', { class: 'card-title' }, source.label),
      el('span', { class: 'badge badge-draft row-end' }, source.key),
    ]),
    el('p', { class: 'card-meta' }, message),
    el('p', { class: 'card-meta' }, t.crawlYield(source.yieldLast, source.yieldTotal)),
  ]));
}

function runList(runs) {
  if (!runs.length) return el('div', { class: 'pub-empty' }, t.crawlNoProblems);
  return el('ul', { class: 'card-list' }, runs.slice(0, 8).map((run) => {
    let counts = {};
    try { counts = JSON.parse(run.counts_json ?? '{}'); } catch { counts = {}; }
    return el('li', {}, el('div', { class: 'card' }, [
      el('div', { class: 'row' }, [
        el('h4', { class: 'card-title' }, formatMoment(run.started_at)),
        el('span', {
          class: `badge ${run.status === 'ok' ? 'badge-live' : 'badge-draft'} row-end`,
        }, run.status),
      ]),
      el('p', { class: 'card-meta' }, [
        t.crawlStaged(counts.staged ?? 0),
        counts.promotable !== undefined ? ` · ${t.crawlPromotable(counts.promotable)}` : '',
        run.error ? ` · ${String(run.error).slice(0, 120)}` : '',
      ].join('')),
    ]));
  }));
}
